import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QuoteDTO, QuotesQueryKey } from '@setvi/shared/services/react-query';

/**
 * Centralized cache operations for quote data
 * Eliminates repeated query key construction and cache access patterns
 * Returns a memoized object to prevent unnecessary re-renders
 */
export const useQuoteCache = (quoteId: string) => {
  const queryClient = useQueryClient();

  // Memoize the query key to prevent unnecessary recalculations
  const queryKey = useMemo(
    () => [QuotesQueryKey.QUOTE_DETAILS, quoteId] as const,
    [quoteId]
  );

  return useMemo(
    () => ({
      queryKey,

      /** Get current quote from cache */
      getQuote: () => queryClient.getQueryData<QuoteDTO>(queryKey),

      /** Update quote in cache with an updater function */
      setQuote: (
        updater: (old: QuoteDTO | undefined) => QuoteDTO | undefined
      ) => queryClient.setQueryData<QuoteDTO>(queryKey, updater),

      /** Cancel any outgoing refetches */
      cancel: () => queryClient.cancelQueries({ queryKey }),

      /**
       * Invalidate cache - marks data as stale but doesn't trigger immediate refetch
       * Use this after optimistic updates to sync with server on next mount/focus
       */
      invalidate: () =>
        queryClient.invalidateQueries({
          queryKey,
          refetchType: 'none' // Don't refetch immediately - let staleTime handle it
        }),

      /**
       * Force refetch from server - use sparingly, blocks UI
       * Prefer invalidate() for most cases
       */
      refetch: () => queryClient.refetchQueries({ queryKey }),

      /** Get queryClient for advanced operations */
      queryClient
    }),
    [queryClient, queryKey]
  );
};
